// shareCanvas.js
// Fonte ÚNICA da geração/compartilhamento da imagem de refeição/treino
// (html2canvas). Reusada pelo ShareRefeicao (offcanvas) e pelo prompt pós-
// registro — anti-duplicação.
//
// ── UMA DIFERENÇA DE PROPÓSITO ───────────────────────────────────────────────
// Os ajustes do ESTÚDIO (shareStudio.js) entram só no clone, via
// `aplicarNaCaptura`. A amostra na tela fica no desenho original — a lista
// abaixo é de divergências que eram BUG; essa é a única que é decisão.
//
// ── POR QUE O ARQUIVO SAÍA DIFERENTE DA TELA ─────────────────────────────────
// 1) CAPTURA EM CIMA DO NODE VIVO. O card mora dentro do Swiper e o CSS aplica
//    `transform: scale(...)` nele em telas baixas (`@media (max-height:720px)`
//    → `.cardPreview > .shareCard { transform: scale(.85) }`). O html2canvas
//    dimensiona o canvas pelo getBoundingClientRect (JÁ reduzido) e depois
//    aplica o transform DE NOVO no desenho: o card saía encolhido dentro de um
//    quadro menor — o "cortado". Agora a captura acontece sobre um CLONE fora
//    da tela, sem transform e no tamanho de layout real.
// 2) IMAGEM REMOTA SEM CORS. A foto do card vem do Storage (fluxo de treino).
//    O html2canvas rebaixa a imagem para o iframe clone; sem cabeçalho CORS ela
//    vira ESPAÇO VAZIO no PNG — o card saía "sem foto". Agora os bytes são
//    embutidos como data: URL antes da captura, então não existe origem cruzada
//    nenhuma.
// 2b) FOTO EM 1/16 DA RESOLUÇÃO. O html2canvas rebaixa `background-image` ao
//    tamanho da CAIXA em CSS px antes de ampliar tudo pela escala de exportação
//    — a foto saía de 270px esticada para 1080, enquanto o texto era redesenhado
//    nítido em cima dela. No clone o fundo `cover` vira um `<img>`, que o
//    renderer desenha a partir dos pixels originais. Ver `promoverFundosParaImg`.
// 3) ESPERA DE IMAGEM QUE NUNCA TERMINAVA. `img.complete` também é true para
//    imagem que JÁ FALHOU; pendurar onload ali travava o "Preparando…" para
//    sempre.
// 4) FONTE AINDA NÃO CARREGADA. As métricas de texto do node original definem
//    a quebra de linha da captura — medir com fonte de fallback e renderizar
//    com a definitiva trocava o layout do arquivo.
// 5) ARQUIVO TEMPORÁRIO APAGADO CEDO DEMAIS. O `Share.share` resolve quando a
//    folha do sistema fecha, MAS o app de destino (Instagram) só lê o arquivo
//    depois disso. O `finally` apagava o PNG no meio da leitura → imagem
//    quebrada/preta no Instagram. Agora a limpeza é da SOBRA da vez anterior.
// 6) DOWNLOAD NO ANDROID NATIVO. `<a download>` com blob: não faz nada na
//    WebView (não há DownloadListener) e o objectURL ainda era revogado no
//    mesmo tick — no navegador isso trunca o arquivo. Android nativo agora usa
//    a folha de compartilhamento (como o iOS já fazia) e a web revoga depois.
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { storyExportScale } from '../../../../config/storyCanvas';
import { aplicarNaCaptura, limparEstudio } from './shareStudio';

// Prefixo dos PNGs temporários no Cache — permite varrer as sobras sem
// depender de lembrar nomes entre sessões.
const PREFIXO_TEMP = 'lesvita-share-';

// Teto de espera por imagem: a captura tem de acontecer mesmo com uma imagem
// que nunca resolve (rede caída, URL morta).
const TIMEOUT_IMAGEM_MS = 6000;

// ── fontes ───────────────────────────────────────────────────────────────────
// O html2canvas já espera `fonts.ready` DENTRO do clone, mas quem mede o
// layout que vira o canvas é o documento de fora.
const aguardarFontes = async () => {
  try { await document.fonts?.ready; } catch { /* sem Font Loading API */ }
};

// ── palco fora da tela ───────────────────────────────────────────────────────
// Clone renderizado, porém fora do viewport. NÃO usar opacity/visibility/
// display: o html2canvas respeita os três e devolveria um PNG em branco.
// A media query continua valendo com o MESMO viewport da tela (não mexemos em
// windowWidth/Height de propósito: mudar isso faria o arquivo divergir do que
// o usuário está vendo). O que some é só o transform herdado do carrossel,
// porque no palco o pai não é mais `.cardPreview`.
const montarPalco = (node) => {
  const rect = node.getBoundingClientRect();
  // offsetWidth/Height são o tamanho de LAYOUT (sem transform); o rect é o
  // tamanho já transformado. É o de layout que queremos exportar.
  const largura = Math.round(node.offsetWidth || rect.width);
  const altura = Math.round(node.offsetHeight || rect.height);

  const clone = node.cloneNode(true);
  // Os AJUSTES do estúdio são estilo inline e vêm no clone de propósito — são a
  // arte. A MOLDURA do elemento em edição não: sai daqui para não sair
  // carimbada no story de quem estiver com o editor aberto.
  limparEstudio(clone);
  clone.style.transform = 'none';
  clone.style.margin = '0';
  clone.style.width = `${largura}px`;
  clone.style.height = `${altura}px`;

  const palco = document.createElement('div');
  palco.setAttribute('aria-hidden', 'true');
  palco.style.cssText = [
    'position:fixed',
    'top:0',
    `left:${-(largura + 400)}px`,
    `width:${largura}px`,
    `height:${altura}px`,
    'margin:0', 'padding:0', 'border:0',
    'transform:none',
    'pointer-events:none',
    'z-index:-2147483647',
  ].join(';');
  palco.appendChild(clone);
  // Filho do <body> para herdar as variáveis de tema do :root — as cores do
  // card (--color-accent, --color-bg-subtle…) vêm de lá.
  document.body.appendChild(palco);

  return { palco, clone, largura, altura };
};

// ── imagens ──────────────────────────────────────────────────────────────────
const blobParaDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result));
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const ehRemota = (url) => /^https?:\/\//i.test(url);

// Baixa e devolve data: URL. Falhou (CORS, 404, offline) → null, e a URL
// original é mantida para o html2canvas tentar do jeito dele.
const paraDataUrl = async (url) => {
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
    if (!res.ok) return null;
    return await blobParaDataUrl(await res.blob());
  } catch {
    return null;
  }
};

// Valor CSS de background-image pode ter vários url() e gradientes juntos;
// trocamos só os pedaços url(...) remotos, preservando o resto do valor.
const RE_URL_CSS = /url\((['"]?)([^'")]+)\1\)/g;

const embutirImagensRemotas = async (raiz) => {
  const tarefas = [];

  // <img src>: inclui os .svg do logo (antes tratados por objectURL à parte).
  raiz.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') || '';
    if (!src || src.startsWith('data:')) return;
    // crossOrigin só na remota: forçá-lo numa same-origin dispara um reload
    // sem ganho nenhum.
    if (ehRemota(src)) img.crossOrigin = 'anonymous';
    tarefas.push((async () => {
      const dataUrl = await paraDataUrl(src);
      if (dataUrl) img.setAttribute('src', dataUrl);
    })());
  });

  // background-image (é assim que o ShareCard mostra a foto da refeição/treino).
  const elementos = [raiz, ...raiz.querySelectorAll('*')];
  elementos.forEach((el) => {
    if (!el.style) return;
    const valor = el.style.backgroundImage || window.getComputedStyle(el).backgroundImage;
    if (!valor || valor === 'none' || !valor.includes('url(')) return;

    const urls = [];
    valor.replace(RE_URL_CSS, (_m, _aspas, url) => { urls.push(url); return _m; });
    if (!urls.some(ehRemota)) return;

    tarefas.push((async () => {
      const mapa = new Map();
      await Promise.all(urls.filter(ehRemota).map(async (url) => {
        const dataUrl = await paraDataUrl(url);
        if (dataUrl) mapa.set(url, dataUrl);
      }));
      if (!mapa.size) return;
      el.style.backgroundImage = valor.replace(
        RE_URL_CSS,
        (m, _aspas, url) => (mapa.has(url) ? `url("${mapa.get(url)}")` : m),
      );
    })());
  });

  await Promise.all(tarefas);
};

// `complete` é true tanto para carregada quanto para QUEBRADA — por isso a
// espera precisa de saída por evento E por tempo.
const aguardarImagens = (raiz) => Promise.all(
  Array.from(raiz.querySelectorAll('img')).map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      let feito = false;
      const fim = () => { if (!feito) { feito = true; resolve(); } };
      img.addEventListener('load', fim, { once: true });
      img.addEventListener('error', fim, { once: true });
      setTimeout(fim, TIMEOUT_IMAGEM_MS);
    });
  }),
);

// ── backdrop-filter ──────────────────────────────────────────────────────────
// O html2canvas não implementa backdrop-filter: os painéis "de vidro" saíam
// como retângulos translúcidos sobre a foto e o texto ficava ilegível no
// arquivo. Aproxima o efeito no CLONE (nunca na tela) subindo o alfa do fundo,
// que é o que o blur faz na prática: reduzir o contraste do que está atrás.
const ALFA_MINIMO_VIDRO = 0.86;
const RE_RGBA = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?\s*\)$/i;

const aproximarVidro = (raiz) => {
  [raiz, ...raiz.querySelectorAll('*')].forEach((el) => {
    if (!el.style) return;
    const css = window.getComputedStyle(el);
    const filtro = css.backdropFilter || css.webkitBackdropFilter;
    if (!filtro || filtro === 'none') return;

    const m = RE_RGBA.exec(css.backgroundColor || '');
    if (!m) return;
    const alfa = m[4] === undefined ? 1 : Number(m[4]);
    if (!(alfa > 0) || alfa >= ALFA_MINIMO_VIDRO) return;
    el.style.backgroundColor = `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${ALFA_MINIMO_VIDRO})`;
  });
};

// ── ícones SVG ───────────────────────────────────────────────────────────────
// O html2canvas não desenha SVG inline junto com o resto: ele SERIALIZA o nó
// (XMLSerializer) e pinta o resultado como imagem. Fora do documento, some tudo
// que dependia de contexto:
//   • `stroke="currentColor"` — como os ícones do react-icons vêm — perde o
//     `color` do CSS e cai no preto. Era isso que fazia o "certinho" do 1r
//     desaparecer: ele saía preto sobre o vidro escuro.
//   • `.ringTrack` / `.ringValue` são CLASSES de CSS Module; num SVG solto não
//     existe folha de estilo, e os círculos do anel viravam disco preto.
// A saída é gravar o valor JÁ RESOLVIDO no `style` do próprio nó: o atributo
// `style` viaja na serialização, vence o atributo de apresentação
// (`stroke="currentColor"`) na cascata e aceita as unidades que o computed
// devolve — `stroke-dasharray: 232.06px 283px` como ATRIBUTO seria arriscado,
// como CSS é o formato nativo. No clone, nunca na tela.
const PINTURA_SVG = [
  'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-dashoffset', 'opacity',
];

const fixarIconesSvg = (raiz) => {
  raiz.querySelectorAll('svg').forEach((svg) => {
    [svg, ...svg.querySelectorAll('*')].forEach((no) => {
      const css = window.getComputedStyle(no);
      PINTURA_SVG.forEach((prop) => {
        const valor = css.getPropertyValue(prop);
        // `none` é valor legítimo (o traço dos ícones vive sobre fill:none) —
        // só o vazio é ignorado.
        if (valor) no.style.setProperty(prop, valor.trim());
      });
    });
  });
};

// ── recorte de uma linha ─────────────────────────────────────────────────────
// `overflow: hidden` + `white-space: nowrap` é o par que faz o "…" do CSS. A
// caixa desses blocos tem a altura de EXATAMENTE uma linha, e o html2canvas
// desenha o texto um pouco mais baixo que o navegador — o resultado é a barriga
// das letras cortada no arquivo (na tela nada corta: lá quem desenha é o
// navegador). O nome do treino, a linha da atividade e o rodapé "1h05 · 438
// kcal" saíam decapitados por isso.
//
// A saída é fazer o "…" na mão AQUI, no clone, e só então soltar o recorte: com
// o texto já cabendo na largura, não há o que cortar na horizontal — e a
// vertical para de cortar junto. De propósito NÃO se mexe no tamanho da caixa:
// qualquer folga de altura empurraria o layout e invalidaria os ajustes que o
// estúdio salvou.
const RETICENCIA = '…';

const soltarRecorteDeUmaLinha = (raiz) => {
  [raiz, ...raiz.querySelectorAll('*')].forEach((el) => {
    const css = window.getComputedStyle(el);
    // O próprio .shareCard tem overflow:hidden (é o que arredonda a foto), mas
    // não é nowrap — fica de fora, como tem de ser.
    if (css.overflow !== 'hidden' || css.whiteSpace !== 'nowrap') return;

    const cabe = () => el.scrollWidth <= el.clientWidth + 1;

    if (!cabe()) {
      // Só encurtamos folha de texto: bloco com filho-elemento tem layout
      // próprio e cortar caractere ali não faria sentido — esse mantém o corte.
      const filhos = Array.from(el.childNodes);
      const no = filhos.length === 1 && filhos[0].nodeType === 3 ? filhos[0] : null;
      if (!no) return;

      // Busca binária: cada tentativa custa um reflow, então nada de tirar letra
      // por letra de um nome de treino comprido.
      const original = no.data;
      let baixo = 0;
      let alto = original.length;
      while (baixo < alto) {
        const meio = Math.ceil((baixo + alto) / 2);
        no.data = original.slice(0, meio) + RETICENCIA;
        if (cabe()) baixo = meio;
        else alto = meio - 1;
      }
      no.data = baixo > 0 ? original.slice(0, baixo) + RETICENCIA : RETICENCIA;
      if (!cabe()) return; // nem as reticências sozinhas couberam: segue clipado
    }

    el.style.overflow = 'visible';
  });
};

// ── foto de fundo → <img> (resolução real) ───────────────────────────────────
// O "compartilhei e a FOTO saiu borrada, mas as letras não".
//
// O html2canvas NÃO desenha `background-image` na resolução da imagem. Ele
// passa por `resizeImage(image, width, height)`, em que width/height são a
// caixa em CSS px — 270×480, o tamanho do cartão — e só então pinta o pattern
// no contexto já escalado em 4×. Resultado: a foto era REDUZIDA para 270px de
// largura e REAMPLIADA para 1080. Dezesseis vezes menos pixels.
//
// Texto, ícone e degradê não passam por isso: são redesenhados direto no
// contexto escalado, em 1080p. Daí o sintoma exato — informação nítida sobre
// uma foto lavada. E daí também o arquivo sair PIOR que a amostra na tela: num
// aparelho 3x a preview mostra a mesma foto com ~810px reais de fundo.
//
// `<img>` toma outro caminho no renderer (`renderReplacedElement`): um
// `drawImage` dos pixels ORIGINAIS direto no contexto escalado. Por isso a
// troca — e SÓ NO CLONE:
//   • na tela o `background-image` continua, porque lá quem desenha é o
//     navegador, que já usa a foto inteira;
//   • o estúdio endereça elemento por `nome:índice` a partir da raiz
//     (shareStudio.js), então um filho novo no cartão VIVO deslocaria todos os
//     índices e invalidaria os ajustes salvos em shareOverrides.js. No clone
//     não há esse risco: isto roda DEPOIS de `aplicarNaCaptura`, e nenhum passo
//     adiante endereça filho por posição.
//
// POR QUE A CAIXA É CALCULADA AQUI, e não deixada no `object-fit: cover`: o
// html2canvas 1.4.1 não implementa object-fit — ele estica a imagem inteira na
// caixa do elemento. Dando ao <img> a caixa EXATA do recorte "cover" (mesma
// proporção da foto, centrada, sangrando para fora), esticar vira identidade e
// o recorte volta a ser do `overflow: hidden` do cartão.
//
// A foto entra como PRIMEIRO filho: todo bloco de conteúdo do cartão é
// posicionado (`.gradient`, `.leftStats`, `.sideRail`, `.logo`, os painéis), e
// elemento posicionado pinta depois do irmão mais velho — que é onde a foto
// tem de ficar.
//
// Só substitui fundo `cover` de url() ÚNICA. Gradiente, camada múltipla,
// `contain` e `repeat` seguem pelo caminho antigo de propósito: são fundos
// gerados ou repetidos, para os quais o pattern do html2canvas está certo.
const RE_URL_CSS_UNICA = /url\((['"]?)([^'")]+)\1\)/;

const ehFundoDeFotoUnica = (valor) => {
  const v = (valor || '').trim();
  if (!v.startsWith('url(') || !v.endsWith(')')) return false;
  if (v.includes('gradient(')) return false;
  return (v.match(/url\(/g) || []).length === 1;
};

// Resolve com as dimensões naturais ou com null — nunca rejeita: fundo que não
// abre continua sendo problema do html2canvas, como já era.
const dimensoesDaImagem = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () =>
    resolve(img.naturalWidth > 0 && img.naturalHeight > 0
      ? { largura: img.naturalWidth, altura: img.naturalHeight }
      : null);
  img.onerror = () => resolve(null);
  img.src = src;
});

const promoverFundosParaImg = async (raiz) => {
  const alvos = [raiz, ...raiz.querySelectorAll('*')].filter((el) => {
    if (!el.style) return false;
    const css = window.getComputedStyle(el);
    if (css.backgroundSize !== 'cover') return false;
    return ehFundoDeFotoUnica(el.style.backgroundImage || css.backgroundImage);
  });

  await Promise.all(alvos.map(async (el) => {
    const css = window.getComputedStyle(el);
    const valor = el.style.backgroundImage || css.backgroundImage;
    const achado = RE_URL_CSS_UNICA.exec(valor);
    if (!achado) return;
    const src = achado[2];

    // A caixa de `background-size: cover` é a de PADDING (background-origin
    // padrão) — a mesma a que `left/top` de um filho absoluto se referem.
    const largura = el.clientWidth || el.offsetWidth;
    const altura = el.clientHeight || el.offsetHeight;
    if (!(largura > 0 && altura > 0)) return;

    const natural = await dimensoesDaImagem(src);
    if (!natural) return; // não abriu: mantém o fundo como estava

    const escala = Math.max(largura / natural.largura, altura / natural.altura);
    const l = natural.largura * escala;
    const a = natural.altura * escala;

    const img = document.createElement('img');
    img.alt = '';
    img.src = src;
    img.style.cssText = [
      'position:absolute',
      `left:${(largura - l) / 2}px`,
      `top:${(altura - a) / 2}px`,
      `width:${l}px`,
      `height:${a}px`,
      // O CSS do app tem regras largas de <img>; nenhuma pode reapertar a caixa
      // que acabou de ser calculada.
      'min-width:0', 'min-height:0', 'max-width:none', 'max-height:none',
      'margin:0', 'padding:0', 'border:0',
      'object-fit:fill',
      'pointer-events:none',
    ].join(';');

    // O cartão já é `position: relative` + `overflow: hidden`. Garantir os dois
    // deixa a promoção correta para qualquer outro fundo `cover` que apareça.
    if (css.position === 'static') el.style.position = 'relative';
    if (css.overflow !== 'hidden') el.style.overflow = 'hidden';
    el.style.backgroundImage = 'none';
    el.insertBefore(img, el.firstChild);
  }));
};

// ── captura ──────────────────────────────────────────────────────────────────
// Sem `scale` explícito o PNG sai no tamanho do Story (STORY_CANVAS 1080×1920):
// card 270×480 → 4× → 1080×1920. Antes era scale=2 fixo (540×960), metade da
// resolução que o Instagram usa.
export async function nodeToPngBlob(node, { scale } = {}) {
  if (!node) throw new Error('Elemento para capturar não encontrado.');

  await aguardarFontes();
  const { palco, clone, largura } = montarPalco(node);
  const escala = scale || storyExportScale(largura);

  try {
    // Os ajustes do estúdio entram AQUI, no clone — a amostra na tela continua
    // com o desenho original. Tem de ser depois do palco ir para o documento:
    // compor o transform sem quebrar o do CSS exige ler o computed style.
    aplicarNaCaptura(clone);
    await embutirImagensRemotas(clone);
    aproximarVidro(clone);
    fixarIconesSvg(clone);
    // Depois dos ajustes: um patch de fonte ou de largura muda o que cabe.
    soltarRecorteDeUmaLinha(clone);
    // Por último: a caixa do recorte sai do tamanho JÁ ajustado do cartão.
    await promoverFundosParaImg(clone);
    await aguardarImagens(clone);

    const canvas = await html2canvas(clone, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      scale: escala,
      logging: false,
    });
    // PNG (não WebP): é o formato que todo destino aceita — Instagram inclusive.
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } finally {
    palco.remove();
  }
}

// ── arquivos temporários (nativo) ────────────────────────────────────────────
// Varre as sobras das vezes anteriores. NÃO apagamos o arquivo da vez atual
// logo depois do Share: o app de destino ainda vai lê-lo.
async function limparTemporarios() {
  try {
    const { files } = await Filesystem.readdir({ directory: Directory.Cache, path: '' });
    await Promise.all((files || []).map(async (f) => {
      const nome = typeof f === 'string' ? f : f?.name;
      if (!nome || !nome.startsWith(PREFIXO_TEMP)) return;
      try {
        await Filesystem.deleteFile({ directory: Directory.Cache, path: nome });
      } catch { /* já sumiu */ }
    }));
  } catch { /* Cache sem readdir nesta plataforma — segue */ }
}

// base64 sem o prefixo data: (Filesystem.writeFile espera só o payload).
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function compartilharNativo(blob, name, { title, text }) {
  await limparTemporarios();
  const fileName = `${PREFIXO_TEMP}${Date.now()}-${name}.png`;
  const base64Data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({
    path: fileName, data: base64Data, directory: Directory.Cache,
  });
  // Sem delete aqui de propósito: o Instagram (e qualquer destino) lê o arquivo
  // DEPOIS que a folha fecha. A sobra some na próxima chamada.
  // O retorno traz `activityType` quando a plataforma informa o app escolhido
  // (pacote no Android, UTI no iOS). É DICA, não verdade: nem toda folha
  // devolve, e quem confirma o canal é a pessoa, no modal de recompensa.
  return Share.share({ title, text, url: uri, dialogTitle: 'Compartilhar sua análise' });
}

function baixarBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${name}.png`;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revogar no mesmo tick corta a leitura do arquivo pela metade em alguns
  // navegadores — o arquivo baixado saía truncado/ilegível.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Compartilhar: share sheet nativo (Capacitor) ou Web Share API (com fallback
// download na web quando o navegador não suporta compartilhar arquivos).
//
// Retorno: `{activityType}` quando a plataforma diz para qual app foi (só o
// nativo informa; a Web Share API não expõe o destino). Serve de PISTA para
// pré-selecionar o canal no modal de recompensa — nunca como o valor final.
export async function shareNode(node, name, meta = {}) {
  const { scale, title, text } = {
    title: 'Análise Nutricional LesVita',
    text: 'Confira a análise da minha refeição feita com o app LesVita!',
    ...meta,
  };
  const blob = await nodeToPngBlob(node, { scale });
  if (!blob) throw new Error('Falha ao gerar imagem.');

  if (Capacitor.isNativePlatform()) {
    const res = await compartilharNativo(blob, name, { title, text });
    return { activityType: res?.activityType || '' };
  }

  const file = new File([blob], `${name}.png`, { type: 'image/png' });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ title, text, files: [file] });
  } else {
    baixarBlob(blob, name);
  }
  return { activityType: '' };
}

// Baixar. Em app NATIVO (iOS e Android) o `<a download>` da WebView não salva
// nada — no iOS o WKWebView ignora e no Android não há DownloadListener
// registrado. Nos dois casos o caminho real é a folha do sistema, que oferece
// "Salvar imagem". Só a web faz download de verdade.
export async function downloadNode(node, name, meta = {}) {
  if (Capacitor.isNativePlatform()) {
    return shareNode(node, name, meta);
  }
  const blob = await nodeToPngBlob(node, { scale: meta.scale });
  if (!blob) throw new Error('Falha ao gerar imagem.');
  baixarBlob(blob, name);
}
